import { createModel } from "@rematch/core";
import { api } from "api";
import { CreateUpdateRoleDto, RoleListVm } from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import {
  ListRequestParams,
  mergeListParams,
  mergeListParamsWithPagination,
} from "widgets/table/useTableUtils";

interface RolesListSettingsState {
  roles: RoleListVm[];
  rolesListParams: ListRequestParams;
  editingRoleId?: string;
}

const initialState: RolesListSettingsState = {
  roles: [],
  rolesListParams: {
    pageSize: 10,
  },
  editingRoleId: undefined,
};

export const rolesListSettings = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setRoles(
      state,
      {
        data,
        listParams,
      }: { data: RoleListVm[]; listParams: ListRequestParams }
    ) {
      state.roles = data;
      state.rolesListParams = listParams;
    },
    resetRolesListParams(state) {
      state.rolesListParams = {
        ...initialState.rolesListParams,
      };
    },
    setEditingRoleId(state, data?: string) {
      state.editingRoleId = data;
    },
    resetEditingRoleData(state) {
      state.editingRoleId = undefined;
    },

    reset: () => initialState,
  },
  effects: (dispatch) => {
    const { rolesListSettings } = dispatch;

    return {
      async fetchRoles(params: ListRequestParams = {}, state): Promise<void> {
        const mergedParams = mergeListParams(
          state.rolesListSettings.rolesListParams,
          params
        );

        const data =
          await api.lumen.rolePermissions.apiRolePermissionsGetAllRolesPost({
            getAllRolesQuery: mapToGetAllQuery(mergedParams),
          });

        const { items, ...pagination } = data;

        dispatch(
          rolesListSettings.setRoles({
            data:
              items?.sort(
                (a, b) => Number(b.isBuiltIn) - Number(a.isBuiltIn)
              ) || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async createRole(values: CreateUpdateRoleDto): Promise<void> {
        await api.lumen.rolePermissions.apiRolePermissionsPost({
          createUpdateRoleDto: values,
        });
      },

      async editRole({ id, values }): Promise<void> {
        await api.lumen.rolePermissions.apiRolePermissionsIdPut({
          id,
          createUpdateRoleDto: values,
        });
      },

      async deleteRole(id: string): Promise<void> {
        await api.lumen.rolePermissions.apiRolePermissionsIdDelete({ id });
      },
    };
  },
});
